import logger from './logger';

/**
 * Global error handler for unhandled errors
 */
class ErrorHandler {
  constructor() {
    this.errorQueue = [];
    this.maxQueueSize = 50;
    this.initializeHandlers();
  }

  /**
   * Initialize global error handlers
   */
  initializeHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError({
        type: 'unhandledRejection',
        reason: event.reason,
        promise: event.promise,
        timestamp: new Date().toISOString()
      });
      
      // Prevent default browser behavior in production
      if (process.env.NODE_ENV === 'production') {
        event.preventDefault();
      }
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
      this.handleError({
        type: 'globalError',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        timestamp: new Date().toISOString()
      });
      
      // Prevent default browser behavior in production for certain errors
      if (process.env.NODE_ENV === 'production' && this.shouldSuppressError(event)) {
        event.preventDefault();
      }
    });
  }

  /**
   * Determine if an error should be suppressed
   */
  shouldSuppressError(event) {
    const message = event.message || '';
    
    // Suppress common third-party errors
    const suppressPatterns = [
      /ResizeObserver loop limit exceeded/,
      /Non-Error promise rejection captured/,
      /Network request failed/,
      /Load failed/,
      /Script error/
    ];
    
    return suppressPatterns.some(pattern => pattern.test(message));
  }

  /**
   * Determine if console logging should be suppressed
   */
  shouldSuppressConsoleLog(errorInfo) {
    const message = errorInfo.message || errorInfo.reason || '';
    
    // Additional patterns to suppress from console in production
    const suppressPatterns = [
      /ResizeObserver loop limit exceeded/,
      /Non-Error promise rejection captured/,
      /Network request failed/,
      /Load failed/,
      /Script error/,
      /Failed to fetch/,
      /CORS/,
      /404/,
      /401/,
      /403/
    ];
    
    // Suppress in production only
    return process.env.NODE_ENV === 'production' && 
           suppressPatterns.some(pattern => pattern.test(message));
  }

  /**
   * Determine if an error is serious enough to always log
   */
  isSeriousError(errorInfo) {
    const message = errorInfo.message || errorInfo.reason || '';
    
    const seriousPatterns = [
      /500/,
      /503/,
      /TypeError/,
      /ReferenceError/,
      /SyntaxError/
    ];
    
    return seriousPatterns.some(pattern => pattern.test(message));
  }

  /**
   * Handle and log errors
   */
  handleError(errorInfo) {
    // Check if error should be suppressed
    if (this.shouldSuppressConsoleLog(errorInfo)) {
      return;
    }
    
    // Add to error queue
    this.errorQueue.push(errorInfo);
    
    // Maintain queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }
    
    // Only log in development or for serious errors
    if (process.env.NODE_ENV === 'development' || this.isSeriousError(errorInfo)) {
      if (errorInfo.type === 'unhandledRejection') {
        logger.error('[ErrorHandler] UnhandledRejection:', errorInfo.reason);
      } else if (errorInfo.type === 'globalError') {
        logger.error('[ErrorHandler] GlobalError:', errorInfo.message);
      } else {
        logger.error('[ErrorHandler] UnknownError:', errorInfo);
      }
    }
    
    // Report to analytics if available
    this.reportToAnalytics(errorInfo);
  }

  /**
   * Report errors to analytics service
   */
  reportToAnalytics(errorInfo) {
    if (window.gtag && process.env.NODE_ENV === 'production') {
      try {
        window.gtag('event', 'exception', {
          description: errorInfo.message || errorInfo.reason || 'Unknown error',
          fatal: false,
          error_type: errorInfo.type
        });
      } catch (e) {
        // Silently fail if analytics is not available
      }
    }
  }

  /**
   * Get recent errors for debugging
   */
  getRecentErrors() {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrors() {
    this.errorQueue = [];
  }

  /**
   * Handle API errors specifically
   */
  handleApiError(error, endpoint, method) {
    const apiError = {
      type: 'apiError',
      endpoint,
      method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      data: error.response?.data,
      timestamp: new Date().toISOString()
    };
    
    this.handleError(apiError);
    
    // Return user-friendly error message
    if (error.response) {
      switch (error.response.status) {
        case 400:
          return 'Invalid request. Please check your input and try again.';
        case 401:
          return 'Authentication required. Please log in and try again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested resource was not found.';
        case 429:
          return 'Too many requests. Please wait a moment and try again.';
        case 500:
        case 502:
        case 503:
          return 'Server error. Please try again later.';
        default:
          return 'An unexpected error occurred. Please try again.';
      }
    } else if (error.request) {
      return 'Network error. Please check your connection and try again.';
    } else {
      return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Handle form validation errors
   */
  handleValidationError(errors, formName) {
    const validationError = {
      type: 'validationError',
      formName,
      errors,
      timestamp: new Date().toISOString()
    };
    
    logger.warn('ValidationError', `Form validation failed: ${formName}`, validationError);
    
    return validationError;
  }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

// Export instance and convenience methods
export default errorHandler;

export const handleApiError = (error, endpoint, method) => 
  errorHandler.handleApiError(error, endpoint, method);

export const handleValidationError = (errors, formName) => 
  errorHandler.handleValidationError(errors, formName);

export const getRecentErrors = () => 
  errorHandler.getRecentErrors();

export const clearErrors = () => 
  errorHandler.clearErrors();